import { useRef, useState } from 'react';
import { Badge, Box, Button, Flex, Image, Stack, Text, useToast } from '@chakra-ui/react';

// EG-23/EG-24: upload de selo (imagem) OU guia (PDF) → base64. Mostra, por
// prioridade: arquivo recém-escolhido → arquivo próprio da cert → arquivo padrão
// global (badge "Padrão") → vazio. Suporta resetar para o padrão (onReset).
// onUpload = salva na hora (editar); onChange = deferido (criar).
export default function SealAssetInput({
  label,
  noun = 'selo',
  kind = 'image', // 'image' | 'file'
  accept = '.png,.jpg,.jpeg,.svg',
  currentUrl,
  currentFilename,
  defaultUrl,
  defaultFilename,
  onChange,
  onUpload,
  onReset,
  hint = 'PNG, JPG ou SVG. Recomendado fundo transparente.',
}) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [pickedName, setPickedName] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  async function handleFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    const base64 = await new Promise((resolve) => {
      reader.onload = () => resolve(reader.result?.split(',')[1]);
    });

    if (!base64) {
      toast({ title: 'Não foi possível ler o arquivo', status: 'error' });
      return;
    }

    const asset = { base64, filename: file.name };
    if (kind === 'image') setPreview(`data:${file.type};base64,${base64}`);
    setPickedName(file.name);

    if (onUpload) {
      try {
        setSaving(true);
        await onUpload(asset);
      } catch {
        setPreview(null);
        setPickedName(null);
      } finally {
        setSaving(false);
      }
    } else if (onChange) {
      onChange(asset);
    }
  }

  async function handleReset() {
    if (!onReset) return;
    try {
      setResetting(true);
      await onReset();
      setPreview(null);
      setPickedName(null);
    } catch {
      // erro tratado no onReset (toast)
    } finally {
      setResetting(false);
    }
  }

  const isNew = kind === 'image' ? !!preview : !!pickedName;
  const isOwn = !isNew && (kind === 'image' ? !!currentUrl : !!currentFilename);
  const isDefault =
    !isNew && !isOwn && (kind === 'image' ? !!defaultUrl : !!defaultFilename);

  const shownImage = preview || currentUrl || defaultUrl;
  const shownName = pickedName || currentFilename || defaultFilename;
  const emptyLabel = kind === 'image' ? `Nenhum ${noun}` : `Nenhuma ${noun}`;

  return (
    <Box>
      {label && (
        <Text fontSize='sm' fontWeight={600} color='#5C6066' mb='8px'>
          {label}
        </Text>
      )}
      <Flex align='center' gap='20px'>
        <Box pos='relative' flexShrink={0}>
          <Flex
            w='120px'
            h='120px'
            borderRadius='12px'
            border='2px dashed #DfE3E6'
            bg='#FAFBFC'
            align='center'
            justify='center'
            overflow='hidden'
            p='2'
          >
            {kind === 'image' ? (
              shownImage ? (
                <Image
                  src={shownImage}
                  alt={label || noun}
                  maxW='88%'
                  maxH='88%'
                  opacity={isDefault ? 0.85 : 1}
                />
              ) : (
                <Text fontSize='xs' color='#9CA0A6' textAlign='center' px='2'>
                  {emptyLabel}
                </Text>
              )
            ) : shownName ? (
              <Stack align='center' spacing='6px' opacity={isDefault ? 0.85 : 1}>
                <Flex
                  w='40px'
                  h='48px'
                  bg='#EA582B'
                  color='white'
                  borderRadius='4px'
                  align='center'
                  justify='center'
                  fontSize='11px'
                  fontWeight={700}
                >
                  PDF
                </Flex>
                <Text fontSize='11px' color='#5C6066' noOfLines={2} textAlign='center'>
                  {shownName}
                </Text>
              </Stack>
            ) : (
              <Text fontSize='xs' color='#9CA0A6' textAlign='center' px='2'>
                {emptyLabel}
              </Text>
            )}
          </Flex>
          {isDefault && (
            <Badge
              pos='absolute'
              top='6px'
              left='6px'
              bg='#5C6066'
              color='white'
              fontSize='10px'
              borderRadius='4px'
              px='6px'
            >
              Padrão
            </Badge>
          )}
        </Box>

        <Box>
          <Button
            variant='outline'
            size='medium'
            isLoading={saving}
            loadingText='Salvando'
            onClick={() => inputRef.current?.click()}
          >
            {isOwn || isNew ? `Trocar ${noun}` : `Selecionar ${noun}`}
          </Button>

          {onReset && isOwn && (
            <Button
              variant='ghost'
              size='sm'
              mt='8px'
              ml='2'
              color='#5C6066'
              onClick={handleReset}
              isLoading={resetting}
            >
              Resetar para o padrão
            </Button>
          )}

          <Text fontSize='xs' color='#9CA0A6' mt='8px' maxW='240px'>
            {isNew && !onUpload && `${noun} selecionado. Será aplicado ao salvar. `}
            {isDefault && `Sem ${noun} próprio — o padrão será usado no download. `}
            {hint}
          </Text>
        </Box>

        <input
          type='file'
          ref={inputRef}
          accept={accept}
          style={{ display: 'none' }}
          onChange={handleFile}
        />
      </Flex>
    </Box>
  );
}
